import React from "react";
import { Grid, Paper, Typography, Button, Box, Divider, IconButton, Tooltip, Stack } from "@mui/material";
import { useFieldArray } from "react-hook-form";
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { CustomTextField } from "@/shared/components/ui";

type ClienteItemProps = {
  control: any;
  clienteIndex: number;
  errores: any;
};

const ClienteItem: React.FC<ClienteItemProps> = ({
  control,
  clienteIndex,
  errores,
}) => {

  const {
    fields: sucursales,
    append: appendSucursal,
    remove: removeSucursal,
  } = useFieldArray({
    control,
    name: `clientes.${clienteIndex}.sucursales`,
  });

  const errCliente = errores?.clientes?.[clienteIndex];

  return (
    <Paper elevation={2} sx={{ width: "100%", p: { xs: 2, md: 4 }, mb: 4, mt: 2, borderRadius: 2 }}>

      {/* DATOS DEL CLIENTE */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <PersonOutlineIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Datos del Cliente
        </Typography>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <CustomTextField
            label="Cliente / Oficina"
            name={`clientes.${clienteIndex}.cliente_oficina`}
            control={control}
            error={!!errCliente?.cliente_oficina}
            helperText={errCliente?.cliente_oficina?.message ?? ''}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField
            label="Ciudad"
            name={`clientes.${clienteIndex}.ciudad`}
            control={control}
            error={!!errCliente?.ciudad}
            helperText={errCliente?.ciudad?.message ?? ''}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField
            label="Teléfono"
            name={`clientes.${clienteIndex}.telefono`}
            control={control}
            error={!!errCliente?.telefono}
            helperText={errCliente?.telefono?.message ?? ''}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            label="Dirección"
            name={`clientes.${clienteIndex}.direccion`}
            control={control}
            error={!!errCliente?.direccion}
            helperText={errCliente?.direccion?.message ?? ''}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            label="Contacto Sitio"
            name={`clientes.${clienteIndex}.contacto_sitio`}
            control={control}
            error={!!errCliente?.contacto_sitio}
            helperText={errCliente?.contacto_sitio?.message ?? ''}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* SUCURSALES */}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <StorefrontIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Sucursales
          </Typography>
        </Stack>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            appendSucursal({ nombre_sucursal: "", velocidad: 0, servicio: "", coordenadas: "" })
          }
        >
          Agregar sucursal
        </Button>
      </Stack>

      <Stack spacing={2}>
        {sucursales.map((sucursal, sucursalIndex) => {
          const errSuc = errCliente?.sucursales?.[sucursalIndex];
          return (
            <Box
              key={sucursal.id}
              sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: '#fbfbff' }}
            >
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Sucursal"
                    name={`clientes.${clienteIndex}.sucursales.${sucursalIndex}.nombre_sucursal`}
                    control={control}
                    error={!!errSuc?.nombre_sucursal}
                    helperText={errSuc?.nombre_sucursal?.message ?? ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <CustomTextField
                    label="Velocidad"
                    type="number"
                    name={`clientes.${clienteIndex}.sucursales.${sucursalIndex}.velocidad`}
                    control={control}
                    error={!!errSuc?.velocidad}
                    helperText={errSuc?.velocidad?.message ?? ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Servicio"
                    name={`clientes.${clienteIndex}.sucursales.${sucursalIndex}.servicio`}
                    control={control}
                    error={!!errSuc?.servicio}
                    helperText={errSuc?.servicio?.message ?? ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    type="coordenada"
                    label="Coordenadas"
                    name={`clientes.${clienteIndex}.sucursales.${sucursalIndex}.coordenadas`}
                    control={control}
                    error={!!errSuc?.coordenadas}
                    helperText={errSuc?.coordenadas?.message ?? ''}
                  />
                </Grid>
                <Grid item xs={12} md={1} display="flex" justifyContent={{ xs: 'flex-end', md: 'center' }} alignItems="center">
                  <Tooltip title={sucursales.length === 1 ? "Debe existir al menos una sucursal" : "Eliminar sucursal"}>
                    <span>
                      <IconButton
                        color="error"
                        disabled={sucursales.length === 1}
                        onClick={() => removeSucursal(sucursalIndex)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default ClienteItem;
